import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { Store } from '@ngrx/store';
import { selectUserPrefsNumberOfRows } from 'src/app/core/state/core.selectors';
import { globalVar } from '../../global_variables';
import { FILTER_WILDCARD } from '../../utils/regexp-utils';
import { SearchPagingParams } from './models/SearchPagingParams';
import { SeraDropDownAction } from './models/SeraCustomActions';
import { SeraTableLoader } from './models/SeraTableLoader';
import { SeraTableAction } from './models/SeraTableAction';
import { ImageItem } from '../sera-image/models/image-item';
import { TrafficLightColor } from '../../../analysis/models/criterions/TrafficLightColor';
import { MaternityRisk } from '../../../analysis/models/MaternityRisk';
import { Table, TableSelectAllChangeEvent } from 'primeng/table';

@Component({
    selector: 'sera-table',
    templateUrl: './sera-table.component.html',
    styleUrls: ['./sera-table.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class SeraTableComponent implements OnInit {
    private readonly ORDER_DIRECTION_ASC = 'asc';
    private readonly ORDER_DIRECTION_DESC = 'desc';

    protected readonly globalVar = globalVar;

    @Input()
    customSeraCell: TemplateRef<any> | undefined;

    @Input()
    content!: SeraTableLoader;

    @Input()
    tableTitle?: string;

    @Input()
    tableClass?: string;

    @Input()
    showToolbarButtons: boolean | undefined = false;

    @Input()
    ellipsisOnHeader: boolean = false;

    @Input()
    disableAllTableActions: boolean = false;

    @Input()
    header: boolean = true;

    @Input()
    onlyTitleHeader?: boolean = false;

    @Input()
    hasSearchOption?: boolean = true;

    @Input()
    resizableColumns?: boolean = true;

    filterKeys!: string[];

    @Input()
    isLazy: boolean = false;

    @Input()
    scrollable: boolean = false;

    @Input()
    scrollHeight?: string;

    @Input()
    paginator: boolean = true;

    @Input()
    totalRecords!: number;

    @Input()
    isSelection: boolean = false;

    @Input()
    isSelectAll: boolean = false;

    /** When used, overrides the default header checkbox behavior when selecting/unselecting rows.
     * Toggling the header checkbox will still change the value of selectAll.
     * Should be used for lazy-loaded tables that implement custom algorithms for accurate row
     * selection logic across multiple pages. */
    @Input()
    selectAll?: boolean;

    @Input()
    isClearSelectionOnSearch: boolean = false;

    @Input()
    selectedRows!: any;

    @Input()
    rows?: number;

    @Input() groupRows: boolean = false;

    @Input() groupRowsByField?: string;

    @Input()
    emptyStateImage: ImageItem = { id: 1, src: 'assets/images/MM13/empty_table.png', alt: 'Empty State', height: 64, disableInteractions: true };

    @Input()
    noResultsImage: ImageItem = { id: 1, src: 'assets/images/MM13/empty_table.png', alt: 'No Results State', height: 64, disableInteractions: true };

    @Input()
    emptyStateHeightPx: number | null = null;

    @Input()
    hasSearched = false;

    @Input() isEditMode: boolean = false;

    @Input()
    emptyStateMessage: string = 'PAGE.DEFAULT_EMPTY_STATE_TABLE.LABEL';

    @Input()
    noResultsMessage: string = 'PAGE.ANALYSISEARCH.NO_RESULTS_MESSAGE.LBL';

    @Input()
    noResultsSubMessage: string = 'PAGE.ANALYSISEARCH.NO_RESULTS_SUB_MESSAGE.LBL';

    @Input()
    isEmptyStateButtonVisible: boolean = false;

    @Input()
    isEmptyStateButtonDisabled: boolean = false;

    @Input()
    emptyStateButton?: { action: () => void; label: string };

    @Input() dragAndDropEnabled: boolean = false;

    @Input() customSort: boolean = false;

    @Input()
    sortFunction!: (event: any) => any[];

    @Input()
    rowHeight: number = 37;

    @Input()
    titleSize: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' = 'h5';

    @Input()
    isLoading: boolean = false;

    @Input()
    frozenSelection: boolean = false;

    @Input()
    rowClassFn?: (rowData: any) => string;

    @Input()
    searchPlaceHolder: string = 'PAGE.GENERAL.SEARCH.LBL';

    @Input()
    infoMessage: string | null = null;

    @Output()
    lazyLoaded = new EventEmitter<SearchPagingParams>();

    @Output() rowDragged = new EventEmitter<any>();

    @Output() selectedRowsChange = new EventEmitter<any[]>();

    @Output() selectAllChange = new EventEmitter<boolean>();

    @Output() rowsSorted = new EventEmitter<any[]>();

    $userPrefsNumberOfRows = this.store.select(selectUserPrefsNumberOfRows);

    @ViewChild('dt') table!: Table;

    private _selectAllRows: boolean = false;

    get selectAllRows(): boolean {
        return this.selectAll ?? this._selectAllRows;
    }

    set selectAllRows(value: boolean) {
        if (this.selectAll === undefined) {
            this._selectAllRows = value;
        }
        this.selectAllChange.emit(value);
    }

    constructor(private readonly store: Store) {}

    ngOnInit(): void {
        this.filterKeys = this.content?.cols.map(col => col.field);
    }

    onlazyLoad(event: any) {
        if (this.isLazy) {
            this.lazyLoaded.emit(this.buildSearchParams(event));
        }
    }

    onRowDrag(event: any) {
        if (this.dragAndDropEnabled) {
            this.rowDragged.emit(event);
        }
    }

    onSearch() {
        if (this.isClearSelectionOnSearch) {
            this.selectedRows = [];
            this.selectedRowsChange.emit(this.selectedRows);
        }
    }

    onSelectionChange(value = []) {
        this.selectedRows = value;
        if (this.selectAll === undefined) {
            if (this.selectAllRows && value.length < this.content.data.length) {
                this.selectAllRows = false;
            } else if (!this.selectAllRows && value.length === this.content.data.length) {
                this.selectAllRows = true;
            }
        }
        this.selectedRowsChange.emit(this.selectedRows);
    }

    onSelectAllChange(event: TableSelectAllChangeEvent) {
        this.selectAllRows = event.checked;
        this.selectedRows = event.checked ? [...this.content.data] : [];
        this.selectedRowsChange.emit(this.selectedRows);
    }

    isRowSelected(rowData: any): boolean {
        if (!this.content?.dataKey || !Array.isArray(this.selectedRows) || !this.selectedRows.length) return false;
        return this.selectedRows.some(r => r[this.content.dataKey!] === rowData[this.content.dataKey!]);
    }

    handleInputNumberFocus(col: any) {
        if (col.seraTableAction?.onFocus) {
            col.seraTableAction.onFocus(col.field);
        }
    }

    handleInputNumberBlur(rowData: any, seraTableAction: SeraTableAction, value: any) {
        if (seraTableAction?.action) {
            seraTableAction.action(rowData, seraTableAction.value!, value);
        }
    }

    updateRowDataValue(rowData: any, seraTableAction: SeraTableAction | SeraDropDownAction, value: any, isToCallAction: boolean = false) {
        if (Object.isFrozen(rowData)) {
            rowData = { ...rowData, [seraTableAction.value!]: value };
        } else {
            rowData[seraTableAction.value!] = value;
        }
        if (isToCallAction) {
            if (seraTableAction && seraTableAction.action) {
                seraTableAction.action(rowData, seraTableAction.value!, value);
            }
        }
    }

    // onSelectAll(event: any) {} for now is not used

    initPagingParams() {
        let params: SearchPagingParams = {
            currentPage: 1,
            filter: FILTER_WILDCARD,
            pageSize: 10
        };
        return params;
    }

    buildSearchParams(event: any) {
        let searchPagingParams: SearchPagingParams = this.initPagingParams();
        if (event) {
            let first = event.first ? event.first : 0;
            let rows = event.rows ? event.rows : 10;
            let filter = event.globalFilter ? FILTER_WILDCARD + event.globalFilter + FILTER_WILDCARD : FILTER_WILDCARD;

            searchPagingParams.currentPage = first / rows + 1;
            searchPagingParams.filter = filter;
            searchPagingParams.pageSize = rows;

            if (event.sortField) {
                searchPagingParams.sortColumn = {
                    column: event.sortField,
                    direction: event.sortOrder && event.sortOrder === 1 ? this.ORDER_DIRECTION_ASC : this.ORDER_DIRECTION_DESC
                };
            }
        }
        return searchPagingParams;
    }

    reset(): void {
        this.selectAllRows = false;
        this.selectedRows = [];
        this.selectedRowsChange.emit(this.selectedRows);
        this.table.reset();
    }

    getIconClass(rowData: any, icon: any) {
        return rowData[icon.changeableClassUsingRowDataProperty] ? icon.iconClassToChange : icon.defaultIconClass;
    }

    getIconDisabled(rowData: any, icon: any) {
        return rowData[icon.disableActionUsingRowDataProperty];
    }

    getValueSelectedOfDropdown(rowData: any, dropdown: SeraDropDownAction): string {
        return dropdown.options.find(option => rowData[dropdown.value!] === option.key)?.name || '';
    }

    getToneForSHIValue(color: any) {
        switch (color) {
            case MaternityRisk.NO:
            case TrafficLightColor.GREEN:
                return 'positive';
            case MaternityRisk.MEDIUM:
            case TrafficLightColor.YELLOW:
                return 'caution';
            case MaternityRisk.HIGH:
            case TrafficLightColor.RED:
                return 'critical';
            default:
                return 'muted';
        }
    }
}
